/**
 * Risk Cartography Component
 * Interactive map showing IP geolocations and country risk levels
 * Uses OpenStreetMap + Leaflet for offline-capable mapping
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import {
  Globe, Shield, AlertTriangle, AlertCircle, Info, Users,
  MapPin, RefreshCw, Loader2, ChevronDown, ChevronUp,
  Lock, Scale, Flag, Target, Eye, EyeOff, Filter,
  Maximize2, Minimize2, X, ExternalLink, Database,
  FileDown, Building2, Landmark, Euro, ShieldCheck, FileText
} from 'lucide-react';
import {
  COUNTRY_RISK_DATA,
  getCountryData,
  getThreatLevelColor,
  getThreatLevelLabel,
  GDPR_STATUS
} from '../data/countryRiskData';

// Fix Leaflet default marker icon issue in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const API_BASE = process.env.REACT_APP_API_URL || '';

// Custom marker icons by threat level
const createCustomIcon = (color, size = 24) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
      "></div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Pulse animation for critical markers
const createPulseIcon = (color, size = 24) => {
  return L.divIcon({
    className: 'pulse-marker',
    html: `
      <div style="position: relative;">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: ${size * 2}px;
          height: ${size * 2}px;
          background-color: ${color}40;
          border-radius: 50%;
          animation: pulse 2s infinite;
        "></div>
        <div style="
          position: relative;
          width: ${size}px;
          height: ${size}px;
          background-color: ${color};
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        "></div>
      </div>
      <style>
        @keyframes pulse {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 0.8; }
          100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
        }
      </style>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Map bounds controller
function MapBoundsController({ markers }) {
  const map = useMap();

  // Invalidate map size on mount and periodically to fix display issues
  useEffect(() => {
    // Initial invalidation with multiple delays to ensure proper rendering
    const timeouts = [100, 300, 500, 1000, 2000].map(delay =>
      setTimeout(() => {
        if (map && map.getContainer()) {
          map.invalidateSize({ animate: false });
        }
      }, delay)
    );

    // Also use ResizeObserver to detect container visibility changes
    const container = map.getContainer();
    if (container && typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        setTimeout(() => {
          map.invalidateSize({ animate: false });
        }, 100);
      });
      resizeObserver.observe(container);

      return () => {
        timeouts.forEach(clearTimeout);
        resizeObserver.disconnect();
      };
    }

    return () => timeouts.forEach(clearTimeout);
  }, [map]);

  useEffect(() => {
    if (markers && markers.length > 0) {
      // Delay bounds fitting to ensure map is properly sized
      setTimeout(() => {
        const bounds = L.latLngBounds(
          markers.map(m => [m.latitude, m.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 6 });
      }, 200);
    }
  }, [markers, map]);

  return null;
}

// IP Location Card Component
function IPLocationCard({ location, onClose }) {
  const countryData = getCountryData(location.country_code);

  return (
    <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-4 mb-3">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-brand-green" />
          <span className="font-mono text-white text-sm">{location.ip}</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-gray-500 text-xs">Pays</span>
          <p className="text-white">{location.country || 'Inconnu'}</p>
        </div>
        <div>
          <span className="text-gray-500 text-xs">Ville</span>
          <p className="text-white">{location.city || 'Inconnue'}</p>
        </div>
        <div>
          <span className="text-gray-500 text-xs">ISP</span>
          <p className="text-white truncate">{location.isp || 'Inconnu'}</p>
        </div>
        <div>
          <span className="text-gray-500 text-xs">ASN</span>
          <p className="text-white">{location.asn || 'N/A'}</p>
        </div>
      </div>

      {countryData && (
        <div className="mt-3 pt-3 border-t border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: getThreatLevelColor(countryData.threatLevel) }}
            />
            <span className="text-xs text-gray-400">
              Niveau de menace: {getThreatLevelLabel(countryData.threatLevel)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: countryData.gdpr?.color || '#6B7280' }}
            />
            <span className="text-xs text-gray-400">
              RGPD: {countryData.gdpr?.description || 'Non evalue'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Country Risk Detail Panel
function CountryRiskPanel({ countryCode, onClose }) {
  const country = getCountryData(countryCode);
  if (!country) return null;

  return (
    <div className="absolute top-4 right-4 w-96 max-h-[calc(100%-2rem)] overflow-y-auto bg-brand-black-light border border-brand-green/30 rounded-xl shadow-2xl z-[1000]">
      {/* Header */}
      <div className="sticky top-0 bg-brand-black-light border-b border-brand-green/20 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Flag className="w-5 h-5 text-brand-green" />
            <h3 className="text-lg font-bold text-white">{country.name}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Threat Level */}
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${getThreatLevelColor(country.threatLevel)}20` }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" style={{ color: getThreatLevelColor(country.threatLevel) }} />
            <span className="text-sm font-semibold text-white">Niveau de menace</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: getThreatLevelColor(country.threatLevel) }}
            />
            <span className="text-sm" style={{ color: getThreatLevelColor(country.threatLevel) }}>
              {getThreatLevelLabel(country.threatLevel)}
            </span>
          </div>
        </div>

        {/* GDPR Status */}
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${country.gdpr?.color || '#6B7280'}20` }}>
          <div className="flex items-center gap-2 mb-2">
            <Scale className="w-4 h-4" style={{ color: country.gdpr?.color || '#6B7280' }} />
            <span className="text-sm font-semibold text-white">Protection des donnees</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: country.gdpr?.color || '#6B7280' }}
            />
            <span className="text-sm" style={{ color: country.gdpr?.color || '#6B7280' }}>
              {country.gdpr?.description || 'Non evalue'}
            </span>
          </div>
          {country.dataProtectionAuthority && (
            <p className="mt-2 text-xs text-gray-400">
              Autorite: {country.dataProtectionAuthority}
            </p>
          )}
        </div>

        {/* Hacker Groups */}
        {country.hackerGroups && country.hackerGroups.length > 0 && (
          <div className="p-3 bg-red-500/10 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-red-400" />
              <span className="text-sm font-semibold text-white">
                Groupes de hackers connus ({country.hackerGroups.length})
              </span>
            </div>
            <div className="space-y-1">
              {country.hackerGroups.map((group, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-red-300">
                  <Target className="w-3 h-3" />
                  <span>{group}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cyber Strategy */}
        {country.cyberStrategy && (
          <div className="p-3 bg-blue-500/10 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-white">Strategie cyber</span>
            </div>
            <p className="text-xs text-gray-300">{country.cyberStrategy}</p>
          </div>
        )}

        {/* Notes */}
        {country.notes && (
          <div className="p-3 bg-gray-700/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-semibold text-white">Notes</span>
            </div>
            <p className="text-xs text-gray-300">{country.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Main Risk Cartography Component
export default function RiskCartography({ scanIds = [], workspaceId = null, compact = false }) {
  const [ipLocations, setIpLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(!compact);
  const [fullscreen, setFullscreen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedIP, setSelectedIP] = useState(null);
  const [filters, setFilters] = useState({
    threatLevel: 'all', // all, critical, high, medium, low
    showGDPRIssues: false,
  });
  const [showLegend, setShowLegend] = useState(true);
  const [countryStats, setCountryStats] = useState({});
  const [exporting, setExporting] = useState(false);
  const mapRef = useRef(null);

  // Fetch IP locations from backend
  const fetchIPLocations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get IPs from scans
      const params = {};
      if (scanIds.length > 0) {
        params.scan_ids = scanIds.join(',');
      }
      if (workspaceId) {
        params.workspace_id = workspaceId;
      }

      const response = await axios.get(`${API_BASE}/api/v1/cartography/ip-locations`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.data && response.data.locations) {
        setIpLocations(response.data.locations);

        // Calculate country statistics
        const stats = {};
        response.data.locations.forEach(loc => {
          const cc = loc.country_code;
          if (cc) {
            if (!stats[cc]) {
              stats[cc] = { count: 0, ips: [] };
            }
            stats[cc].count++;
            stats[cc].ips.push(loc.ip);
          }
        });
        setCountryStats(stats);
      }
    } catch (err) {
      console.error('Failed to fetch IP locations:', err);
      // Generate demo data if API fails
      generateDemoData();
    } finally {
      setLoading(false);
    }
  }, [scanIds, workspaceId]);

  // Generate demo data for preview
  const generateDemoData = () => {
    const demoLocations = [
      { ip: '192.168.1.1', latitude: 48.8566, longitude: 2.3522, country: 'France', country_code: 'FR', city: 'Paris', isp: 'Orange', threat_level: 'low' },
      { ip: '10.0.0.1', latitude: 52.5200, longitude: 13.4050, country: 'Allemagne', country_code: 'DE', city: 'Berlin', isp: 'Deutsche Telekom', threat_level: 'low' },
      { ip: '8.8.8.8', latitude: 37.7749, longitude: -122.4194, country: 'Etats-Unis', country_code: 'US', city: 'San Francisco', isp: 'Google', threat_level: 'medium' },
      { ip: '185.199.108.153', latitude: 51.5074, longitude: -0.1278, country: 'Royaume-Uni', country_code: 'GB', city: 'London', isp: 'GitHub', threat_level: 'low' },
      { ip: '104.18.32.7', latitude: 55.7558, longitude: 37.6173, country: 'Russie', country_code: 'RU', city: 'Moscou', isp: 'Unknown', threat_level: 'critical' },
      { ip: '203.0.113.50', latitude: 39.9042, longitude: 116.4074, country: 'Chine', country_code: 'CN', city: 'Beijing', isp: 'China Telecom', threat_level: 'critical' },
    ];
    setIpLocations(demoLocations);

    const stats = {};
    demoLocations.forEach(loc => {
      const cc = loc.country_code;
      if (cc) {
        if (!stats[cc]) stats[cc] = { count: 0, ips: [] };
        stats[cc].count++;
        stats[cc].ips.push(loc.ip);
      }
    });
    setCountryStats(stats);
  };

  // Stringify scanIds to avoid infinite loop from array reference changes
  const scanIdsKey = JSON.stringify(scanIds);

  useEffect(() => {
    fetchIPLocations();
  }, [scanIdsKey, workspaceId]); // eslint-disable-line

  // Filter markers based on current filters
  const filteredLocations = useMemo(() => {
    return ipLocations.filter(loc => {
      const countryData = getCountryData(loc.country_code);

      // Filter by threat level
      if (filters.threatLevel !== 'all') {
        const threatLevel = countryData?.threatLevel || 'unknown';
        if (threatLevel !== filters.threatLevel) return false;
      }

      // Filter by GDPR issues
      if (filters.showGDPRIssues) {
        const gdprStatus = countryData?.gdpr?.status;
        if (gdprStatus === 'adequate' || gdprStatus === 'EU') return false;
      }

      return true;
    });
  }, [ipLocations, filters]);

  // Get marker icon based on country threat level
  const getMarkerIcon = useCallback((location) => {
    const countryData = getCountryData(location.country_code);
    const threatLevel = countryData?.threatLevel || 'unknown';
    const color = getThreatLevelColor(threatLevel);

    if (threatLevel === 'critical') {
      return createPulseIcon(color, 20);
    }
    return createCustomIcon(color, 16);
  }, []);

  // Statistics summary
  const stats = useMemo(() => {
    const result = {
      total: ipLocations.length,
      countries: Object.keys(countryStats).length,
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      gdprIssues: 0
    };

    ipLocations.forEach(loc => {
      const countryData = getCountryData(loc.country_code);
      if (countryData) {
        const level = countryData.threatLevel;
        if (level === 'critical') result.critical++;
        else if (level === 'high') result.high++;
        else if (level === 'medium') result.medium++;
        else if (level === 'low') result.low++;

        const gdprStatus = countryData.gdpr?.status;
        if (gdprStatus !== 'adequate' && gdprStatus !== 'EU') {
          result.gdprIssues++;
        }
      }
    });

    return result;
  }, [ipLocations, countryStats]);

  // Export PDF function
  const exportToPDF = async () => {
    setExporting(true);
    try {
      // Collect all hacker groups from countries with IPs
      const hackerGroups = new Set();
      const countriesWithIssues = [];

      Object.keys(countryStats).forEach(code => {
        const country = getCountryData(code);
        if (country) {
          if (country.hackerGroups) {
            country.hackerGroups.forEach(g => hackerGroups.add(g));
          }
          if (country.gdpr?.status !== 'adequate' && country.gdpr?.status !== 'EU') {
            countriesWithIssues.push({
              code,
              name: country.name,
              gdprStatus: country.gdpr?.description,
              threatLevel: country.threatLevel,
              ips: countryStats[code].count
            });
          }
        }
      });

      // Build PDF content
      const reportData = {
        title: 'Rapport Cartographie des Risques et Conformite',
        generated_at: new Date().toISOString(),
        summary: {
          total_ips: stats.total,
          total_countries: stats.countries,
          critical_countries: stats.critical,
          high_risk_countries: stats.high,
          gdpr_issues: stats.gdprIssues
        },
        ip_locations: ipLocations.map(loc => ({
          ip: loc.ip,
          country: loc.country,
          country_code: loc.country_code,
          city: loc.city,
          isp: loc.isp,
          threat_level: getCountryData(loc.country_code)?.threatLevel || 'unknown'
        })),
        countries_analysis: Object.keys(countryStats).map(code => {
          const country = getCountryData(code);
          return {
            code,
            name: country?.name || code,
            ip_count: countryStats[code].count,
            threat_level: country?.threatLevel,
            gdpr_status: country?.gdpr?.description,
            data_protection_authority: country?.dataProtectionAuthority,
            hacker_groups: country?.hackerGroups || [],
            cyber_strategy: country?.cyberStrategy,
            notes: country?.notes
          };
        }),
        active_hacker_groups: Array.from(hackerGroups),
        gdpr_issues: countriesWithIssues,
        compliance_regulations: [
          {
            name: 'RGPD',
            full_name: 'Règlement Général sur la Protection des Données',
            description: 'Règlement européen sur la protection des données personnelles. Impose des obligations strictes sur le traitement des données personnelles des citoyens européens.',
            requirements: [
              'Consentement explicite pour le traitement des donnees',
              'Droit a l\'effacement (droit a l\'oubli)',
              'Notification des violations dans les 72h',
              'Nomination d\'un DPO si necessaire',
              'Analyses d\'impact sur la vie privee (AIPD)'
            ],
            sanctions: 'Jusqu\'a 20 millions d\'euros ou 4% du CA mondial',
            applicable_if: 'Traitement de données de résidents UE'
          },
          {
            name: 'NIS2',
            full_name: 'Network and Information Security Directive 2',
            description: 'Renforcement de la cybersécurité pour les secteurs critiques et importants de l\'UE. Entrée en vigueur octobre 2024.',
            requirements: [
              'Mesures de gestion des risques cyber',
              'Notification des incidents significatifs',
              'Sécurité de la chaîne d\'approvisionnement',
              'Tests de pénétration et audits',
              'Formation du personnel'
            ],
            sanctions: 'Jusqu\'à 10 millions d\'euros ou 2% du CA mondial',
            applicable_if: 'Entités essentielles ou importantes (énergie, transport, santé, finance, etc.)'
          },
          {
            name: 'ISO 27001',
            full_name: 'Système de Management de la Sécurité de l\'Information',
            description: 'Norme internationale pour les systèmes de management de la sécurité de l\'information (SMSI).',
            requirements: [
              'Politique de sécurité documentée',
              'Évaluation et traitement des risques',
              'Contrôles de sécurité (Annexe A)',
              'Audits internes réguliers',
              'Amélioration continue'
            ],
            sanctions: 'Perte de certification, impact commercial',
            applicable_if: 'Toute organisation souhaitant démontrer sa maturité sécurité'
          },
          {
            name: 'DORA',
            full_name: 'Digital Operational Resilience Act',
            description: 'Résilience opérationnelle numérique pour le secteur financier européen. Application janvier 2025.',
            requirements: [
              'Gestion des risques TIC',
              'Tests de résilience opérationnelle',
              'Gestion des incidents TIC',
              'Gestion des risques liés aux tiers',
              'Partage d\'informations sur les menaces'
            ],
            sanctions: 'Sanctions administratives et pénalités financières',
            applicable_if: 'Établissements financiers, prestataires TIC critiques'
          },
          {
            name: 'CaRE',
            full_name: 'Cadre de Reference Cyber pour les Etablissements de Sante',
            description: 'Cadre de référence français pour l\'évaluation de la maturité cybersécurité des établissements de santé.',
            requirements: [
              'Gouvernance de la sécurité',
              'Protection des donnees de sante',
              'Détection et réponse aux incidents',
              'Continuite d\'activite',
              'Sensibilisation du personnel'
            ],
            sanctions: 'Non-conformite aux exigences HDS',
            applicable_if: 'Etablissements de sante francais'
          }
        ],
        financial_risks: {
          description: 'Analyse des impacts economiques lies a l\'hebergement de donnees et aux cyberattaques',
          data_hosting_risks: [
            {
              scenario: 'Hebergement hors UE sans adequation',
              risk: 'Violation RGPD, transferts illegaux',
              impact_financier: 'Amendes jusqu\'a 4% du CA + actions collectives'
            },
            {
              scenario: 'Hebergement dans pays a risque cyber eleve',
              risk: 'Exposition aux APT, espionnage industriel',
              impact_financier: 'Vol de propriete intellectuelle, perte d\'avantage concurrentiel'
            },
            {
              scenario: 'Fournisseur cloud non certifie',
              risk: 'Non-conformite sectorielle (HDS, SecNumCloud)',
              impact_financier: 'Perte de marches publics, sanctions reglementaires'
            }
          ],
          cyberattack_costs: {
            ransomware: 'Coût moyen : 1,85M EUR (rançon + remédiation + arrêt)',
            data_breach: 'Coût moyen : 4,45M EUR par incident',
            business_interruption: 'Perte moyenne : 1,5M EUR par jour d\'arrêt',
            reputation_damage: 'Perte de 5-7% de la valeur client sur 3 ans'
          }
        }
      };

      // Call backend to generate PDF
      const response = await axios.post(
        `${API_BASE}/api/v1/cartography/export-pdf`,
        reportData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-cartographie-risques-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez reessayer.');
    } finally {
      setExporting(false);
    }
  };

  if (!expanded && compact) {
    return (
      <div className="bg-brand-black-light rounded-xl border border-brand-green/20 p-4">
        <button
          onClick={() => setExpanded(true)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center">
              <Globe className="w-5 h-5 text-brand-green" />
            </div>
            <div className="text-left">
              <h3 className="text-white font-semibold">Cartographie des Risques</h3>
              <p className="text-sm text-gray-400">{stats.total} IPs dans {stats.countries} pays</p>
            </div>
          </div>
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`bg-brand-black-light rounded-xl border border-brand-green/20 ${
        fullscreen ? 'fixed inset-4 z-50' : ''
      }`}
      style={{ overflow: 'visible' }}
    >
      {/* Header */}
      <div className="p-4 border-b border-brand-green/20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center">
            <Globe className="w-5 h-5 text-brand-green" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Cartographie des Risques et Lois</h3>
            <p className="text-sm text-gray-400">
              Geolocalisation des IPs et niveaux de risque par pays
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchIPLocations}
            disabled={loading}
            className="p-2 rounded-lg bg-brand-green/10 text-brand-green hover:bg-brand-green/20 transition-colors"
            title="Rafraichir"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowLegend(!showLegend)}
            className={`p-2 rounded-lg transition-colors ${
              showLegend ? 'bg-brand-green/20 text-brand-green' : 'bg-gray-700 text-gray-400'
            }`}
            title="Legende"
          >
            <Info className="w-4 h-4" />
          </button>
          <button
            onClick={exportToPDF}
            disabled={exporting || loading}
            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50"
            title="Exporter PDF Conformité"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setFullscreen(!fullscreen)}
            className="p-2 rounded-lg bg-gray-700 text-gray-400 hover:bg-gray-600 transition-colors"
            title={fullscreen ? 'Réduire' : 'Plein écran'}
          >
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          {compact && (
            <button
              onClick={() => setExpanded(false)}
              className="p-2 rounded-lg bg-gray-700 text-gray-400 hover:bg-gray-600 transition-colors"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Statistics Bar */}
      <div className="p-3 bg-brand-black border-b border-brand-green/10 flex items-center gap-4 overflow-x-auto">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-brand-green/10 rounded-lg">
          <MapPin className="w-4 h-4 text-brand-green" />
          <span className="text-sm text-white font-semibold">{stats.total}</span>
          <span className="text-xs text-gray-400">IPs</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg">
          <Flag className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-white font-semibold">{stats.countries}</span>
          <span className="text-xs text-gray-400">pays</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-sm text-red-400 font-semibold">{stats.critical}</span>
          <span className="text-xs text-gray-400">critique</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-500/10 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-orange-400" />
          <span className="text-sm text-orange-400 font-semibold">{stats.high}</span>
          <span className="text-xs text-gray-400">eleve</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 rounded-lg">
          <Shield className="w-4 h-4 text-yellow-400" />
          <span className="text-sm text-yellow-400 font-semibold">{stats.gdprIssues}</span>
          <span className="text-xs text-gray-400">RGPD</span>
        </div>
      </div>

      {/* Filters */}
      <div className="p-3 bg-brand-black border-b border-brand-green/10 flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-xs text-gray-400">Filtres:</span>
        </div>
        <select
          value={filters.threatLevel}
          onChange={(e) => setFilters(prev => ({ ...prev, threatLevel: e.target.value }))}
          className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-brand-green focus:outline-none"
        >
          <option value="all">Tous niveaux</option>
          <option value="critical">Critique</option>
          <option value="high">Eleve</option>
          <option value="medium">Moyen</option>
          <option value="low">Faible</option>
        </select>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.showGDPRIssues}
            onChange={(e) => setFilters(prev => ({ ...prev, showGDPRIssues: e.target.checked }))}
            className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-brand-green focus:ring-brand-green"
          />
          <span className="text-sm text-gray-300">Problemes RGPD uniquement</span>
        </label>
      </div>

      {/* Map Container - 700px height */}
      <div
        className="relative"
        style={{
          minHeight: fullscreen ? 'calc(100% - 200px)' : '700px',
          width: '100%',
          overflow: 'hidden'
        }}
      >
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-brand-black">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-brand-green" />
              <span className="text-gray-400">Chargement de la cartographie...</span>
            </div>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-brand-black">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-400">{error}</p>
              <button
                onClick={fetchIPLocations}
                className="mt-3 px-4 py-2 bg-brand-green text-brand-black rounded-lg font-semibold"
              >
                Reessayer
              </button>
            </div>
          </div>
        ) : (
          <>
            <MapContainer
              key="risk-map"
              ref={mapRef}
              center={[46, 10]}
              zoom={4}
              style={{
                height: '700px',
                width: '100%',
                background: '#1a1a1a',
                position: 'relative',
                zIndex: 1
              }}
              scrollWheelZoom={true}
              zoomControl={true}
              whenReady={(map) => {
                // Force invalidate size when map is ready
                setTimeout(() => {
                  map.target.invalidateSize();
                }, 100);
                setTimeout(() => {
                  map.target.invalidateSize();
                }, 500);
              }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                subdomains="abcd"
                maxZoom={19}
              />

              {filteredLocations.map((location, idx) => (
                <Marker
                  key={`${location.ip}-${idx}`}
                  position={[location.latitude, location.longitude]}
                  icon={getMarkerIcon(location)}
                  eventHandlers={{
                    click: () => setSelectedIP(location),
                  }}
                >
                  <Popup className="custom-popup">
                    <div className="p-2 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-brand-green" />
                        <span className="font-mono text-sm font-bold">{location.ip}</span>
                      </div>
                      <div className="text-sm space-y-1">
                        <p><span className="text-gray-500">Pays:</span> {location.country || 'Inconnu'}</p>
                        <p><span className="text-gray-500">Ville:</span> {location.city || 'Inconnue'}</p>
                        <p><span className="text-gray-500">ISP:</span> {location.isp || 'Inconnu'}</p>
                      </div>
                      {location.country_code && (
                        <button
                          onClick={() => setSelectedCountry(location.country_code)}
                          className="mt-2 w-full px-3 py-1.5 bg-brand-green/20 text-brand-green rounded text-xs font-semibold hover:bg-brand-green/30 transition-colors"
                        >
                          Voir risques du pays
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}

              <MapBoundsController markers={filteredLocations} />
            </MapContainer>

            {/* Legend */}
            {showLegend && (
              <div className="absolute bottom-4 left-4 bg-brand-black/90 border border-brand-green/30 rounded-xl p-4 z-[1000]">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-brand-green" />
                  Legende
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-600" />
                    <span className="text-xs text-gray-300">Critique (RU, CN, KP, IR)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500" />
                    <span className="text-xs text-gray-300">Eleve (BY, VN, PK...)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span className="text-xs text-gray-300">Moyen (US, BR, IN...)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-xs text-gray-300">Faible (UE, CH, JP...)</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <p className="text-xs text-gray-400">RGPD</p>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="text-xs text-gray-300">Adequation UE</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <span className="text-xs text-gray-300">Protection partielle</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <span className="text-xs text-gray-300">Sans adequation</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Country Detail Panel */}
            {selectedCountry && (
              <CountryRiskPanel
                countryCode={selectedCountry}
                onClose={() => setSelectedCountry(null)}
              />
            )}
          </>
        )}
      </div>

      {/* Country List Summary */}
      <div className="p-4 border-t border-brand-green/20 max-h-[200px] overflow-y-auto">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Database className="w-4 h-4 text-brand-green" />
          IPs par pays ({Object.keys(countryStats).length})
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {Object.entries(countryStats)
            .sort((a, b) => b[1].count - a[1].count)
            .map(([code, data]) => {
              const countryData = getCountryData(code);
              return (
                <button
                  key={code}
                  onClick={() => setSelectedCountry(code)}
                  className="flex items-center gap-2 p-2 rounded-lg bg-gray-700/30 hover:bg-gray-700/50 transition-colors text-left"
                >
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getThreatLevelColor(countryData?.threatLevel) }}
                  />
                  <span className="text-sm text-white truncate">
                    {countryData?.name || code}
                  </span>
                  <span className="text-xs text-gray-400 ml-auto">
                    {data.count}
                  </span>
                </button>
              );
            })}
        </div>
      </div>

      {/* Source Attribution */}
      <div className="px-4 pb-3 text-xs text-gray-500 flex items-center gap-2">
        <Info className="w-3 h-3" />
        Sources: CNIL, ANSSI, ENISA, MITRE ATT&CK | Dernière MAJ : Janvier 2026
      </div>
    </div>
  );
}
